import type { ActionFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import invariant from 'tiny-invariant';
import type {
    SaveReactionMutation,
    SaveReactionMutationVariables,
    RemoveReactionMutation,
    RemoveReactionMutationVariables,
    ReactionTypeEnum,
} from 'generated/graphql';
import {
    SaveReactionDocument,
    RemoveReactionDocument,
} from 'generated/graphql';

export const UPDATE_BUILD_HUB_INSPIRATION_ITEM_VOTING_API_ROUTE =
    '/remix-api/app/build-hub-inspiration-item-voting';

const DEFAULT_FAILED_MESSAGE = 'Failed to update reaction';

/**
 * Action to comment on a target item.
 */
export const action: ActionFunction = async ({ request, context }) => {
    const data = await request.formData();

    invariant(
        data.get('targetId'),
        'No Comment Target ID submitted for update.'
    );
    invariant(data.get('reaction'), 'No Reaction submitted for update.');
    invariant(data.get('intent'), 'No Intent submitted for update.');

    const handleSaveReaction = async () => {
        const { data: result } = await context.client.mutate<
            SaveReactionMutation,
            SaveReactionMutationVariables
        >({
            mutation: SaveReactionDocument,
            variables: {
                docID: data.get('targetId') as string,
                reaction: data.get('reaction') as ReactionTypeEnum,
                unique: true,
            },
        });

        if (result?.saveReaction.__typename !== 'BuildHubReaction') {
            throw new Error(
                result?.saveReaction?.Message || DEFAULT_FAILED_MESSAGE
            );
        }

        return result?.saveReaction;
    };

    const handleRemoveReaction = async () => {
        const { data: result } = await context.client.mutate<
            RemoveReactionMutation,
            RemoveReactionMutationVariables
        >({
            mutation: RemoveReactionDocument,
            variables: {
                docID: data.get('targetId') as string,
                reaction: data.get('reaction') as ReactionTypeEnum,
            },
        });

        if (result?.removeReaction.__typename !== 'BuildHubReaction') {
            throw new Error(
                result?.removeReaction?.Message || DEFAULT_FAILED_MESSAGE
            );
        }

        return result?.removeReaction;
    };

    try {
        const result =
            data.get('intent') === 'save'
                ? await handleSaveReaction()
                : await handleRemoveReaction();
        return json(result);
    } catch (error) {
        return json({
            buildHubReaction: {
                __typename: 'ServerError',
                Message:
                    error instanceof Error
                        ? error.message
                        : DEFAULT_FAILED_MESSAGE,
            },
        });
    }
};
